import { injectable } from 'inversify';

import type {
  IPlaneParent,
  PlaneBox,
  PlaneSize,
  Position2D,
} from '../ModelTypes';

@injectable()
export abstract class PlanePositionSizeAbstract {
  private _position: Position2D = { x: 0, y: 0 };
  private _parent?: IPlaneParent;

  setParent = (parent?: IPlaneParent) => {
    this._parent = parent;
  };

  private get _halfSize() {
    return {
      width: this.size.width / 2,
      height: this.size.height / 2,
    };
  }

  get position(): Position2D {
    return this._position;
  }

  setPosition = (position: Position2D) => {
    this._position = position;
  };

  shift = (d: Position2D) => {
    const { x, y } = this.position;
    const { x: dx, y: dy } = d;

    this.setPosition({ x: x + dx, y: y + dy });
  };

  abstract get size(): PlaneSize;

  get box(): PlaneBox {
    const { width, height } = this._halfSize;
    const { x, y } = this.position;

    return {
      top: y + height,
      left: x - width,
      bottom: y - height,
      right: x + width,
    };
  }

  get topLeft(): Position2D {
    const { width, height } = this._halfSize;
    const { x, y } = this.position;

    return { x: x - width, y: y + height };
  }

  setTopLeft = (topLeft: Position2D) => {
    const { width, height } = this._halfSize;
    const { x, y } = topLeft;

    this.setPosition({ x: x + width, y: y - height });
  };

  get worldPosition(): Position2D {
    if (!this._parent) {
      return this.position;
    }

    const { x: parentX, y: parentY } = this._parent.worldPosition;
    const x = this.position?.x ?? 0;
    const y = this.position?.y ?? 0;

    return {
      x: x + parentX,
      y: y + parentY,
    };
  }

  get worldBox(): PlaneBox {
    if (!this._parent) {
      return this.box;
    }

    const { x, y } = this.worldPosition;
    const { width, height } = this._halfSize;

    return {
      left: x - width,
      right: x + width,
      top: y + height,
      bottom: y - height,
    };
  }
}
